import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Col,
    FloatingLabel,
    Form,
    Image,
    InputGroup,
    Row,
    Spinner,
} from 'react-bootstrap';

import Captcha from '@/components/common/Captcha';
import { submitTakedown } from '@/shared/services/takedownApi';

import useSignaturePad from './useSignaturePad';

// Role options
const REQUESTER_ROLES = [
    { value: 'owner', label: 'I am the copyright owner' },
    {
        value: 'agent',
        label: 'I am authorized to act on behalf of the copyright owner',
    },
];

export default function TakedownForm() {
    const fileInputRef = useRef(null);
    const captchaRef = useRef(null);

    // Form state
    const [formData, setFormData] = useState({
        requesterName: '',
        requesterEmail: '',
        requesterAddress: '',
        requesterRole: '',
        copyrightOwnerName: '',
        contentUrl: '',
        contentDescription: '',
        originalWorkDescription: '',
        originalWorkUrl: '',
        goodFaithStatement: false,
        accuracyStatement: false,
        electronicSignature: '',
        evidence: null,
    });

    // State to track which fields have been touched
    const [touched, setTouched] = useState({
        requesterName: false,
        requesterEmail: false,
        requesterAddress: false,
        requesterRole: false,
        copyrightOwnerName: false,
        contentUrl: false,
        contentDescription: false,
        originalWorkDescription: false,
        electronicSignature: false,
    });

    // Validation errors
    const [errors, setErrors] = useState({});

    // UI state
    const [validated, setValidated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [evidencePreview, setEvidencePreview] = useState(null);
    const [requestId, setRequestId] = useState(null);
    const [captchaToken, setCaptchaToken] = useState(null);

    // Callback for the signature pad hook — update form data and mark field touched
    const handleSignatureChange = useCallback((signatureData) => {
        setFormData((prev) => ({
            ...prev,
            electronicSignature: signatureData,
        }));
        setTouched((prev) => ({ ...prev, electronicSignature: true }));
    }, []);

    const { canvasRef, clearSignature } = useSignaturePad({
        onSignatureChange: handleSignatureChange,
    });

    /**
     * Cleanup object URL on unmount to prevent memory leak
     */
    useEffect(() => {
        return () => {
            if (evidencePreview) {
                URL.revokeObjectURL(evidencePreview);
            }
        };
    }, [evidencePreview]);

    /**
     * Validates the form values
     */
    const validate = (values) => {
        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const urlRegex = /^https?:\/\/.+/;

        if (!values.requesterName.trim()) {
            newErrors.requesterName = 'Name is required.';
        }

        if (!values.requesterEmail.trim()) {
            newErrors.requesterEmail = 'Email is required.';
        } else if (!emailRegex.test(values.requesterEmail)) {
            newErrors.requesterEmail = 'Please provide a valid email.';
        }

        if (!values.requesterAddress.trim()) {
            newErrors.requesterAddress = 'Address is required.';
        }

        if (!values.requesterRole) {
            newErrors.requesterRole =
                'Please select your relationship to the copyright.';
        }

        if (
            values.requesterRole === 'agent' &&
            !values.copyrightOwnerName.trim()
        ) {
            newErrors.copyrightOwnerName =
                'Copyright owner name is required when acting as agent.';
        }

        if (!values.contentUrl.trim()) {
            newErrors.contentUrl = 'Content URL is required.';
        } else if (!urlRegex.test(values.contentUrl)) {
            newErrors.contentUrl =
                'Please provide a valid URL starting with http:// or https://';
        }

        if (!values.contentDescription.trim()) {
            newErrors.contentDescription = 'Content description is required.';
        } else if (values.contentDescription.length < 20) {
            newErrors.contentDescription =
                'Please provide a more detailed description (at least 20 characters).';
        }

        if (!values.originalWorkDescription.trim()) {
            newErrors.originalWorkDescription =
                'Original work description is required.';
        } else if (values.originalWorkDescription.length < 20) {
            newErrors.originalWorkDescription =
                'Please provide a more detailed description (at least 20 characters).';
        }

        if (!values.goodFaithStatement) {
            newErrors.goodFaithStatement = 'You must confirm this statement.';
        }

        if (!values.accuracyStatement) {
            newErrors.accuracyStatement = 'You must confirm this statement.';
        }

        if (!values.electronicSignature) {
            newErrors.electronicSignature =
                'Electronic signature is required. Please sign above.';
        }

        return newErrors;
    };

    /**
     * Handles input change and validates
     */
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        const updatedFormData = { ...formData, [name]: newValue };
        setFormData(updatedFormData);
        setErrors(validate(updatedFormData));
    };

    /**
     * Handles input blur to mark field as touched
     */
    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched({ ...touched, [name]: true });
        setErrors(validate(formData));
    };

    /**
     * Handle file selection
     */
    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (file) {
            // Validate file size (10MB max)
            if (file.size > 10 * 1024 * 1024) {
                setSubmitError('Evidence file must be less than 10MB');
                return;
            }

            // Validate file type
            const allowedTypes = [
                'image/jpeg',
                'image/png',
                'image/gif',
                'image/webp',
                'application/pdf',
            ];
            if (!allowedTypes.includes(file.type)) {
                setSubmitError(
                    'Invalid file type. Only JPEG, PNG, GIF, WebP, and PDF are allowed.'
                );
                return;
            }

            setFormData((prev) => ({ ...prev, evidence: file }));

            // Only show preview for images
            if (file.type.startsWith('image/')) {
                // Revoke the old URL before creating a new one
                if (evidencePreview) {
                    URL.revokeObjectURL(evidencePreview);
                }
                setEvidencePreview(URL.createObjectURL(file));
            } else {
                setEvidencePreview(null);
            }
            setSubmitError('');
        }
    };

    /**
     * Remove evidence file
     */
    const handleRemoveEvidence = () => {
        // Revoke the object URL to prevent memory leak
        if (evidencePreview) {
            URL.revokeObjectURL(evidencePreview);
        }
        setFormData((prev) => ({ ...prev, evidence: null }));
        setEvidencePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    /**
     * Clear signature canvas and reset the signature form field
     */
    const handleClearSignature = () => {
        clearSignature();
        const updatedFormData = { ...formData, electronicSignature: '' };
        setFormData(updatedFormData);
        setErrors(validate(updatedFormData));
    };

    /**
     * Handle form submission
     */
    const handleSubmit = async (event) => {
        event.preventDefault();

        const newErrors = validate(formData);
        setErrors(newErrors);
        setTouched({
            requesterName: true,
            requesterEmail: true,
            requesterAddress: true,
            requesterRole: true,
            copyrightOwnerName: true,
            contentUrl: true,
            contentDescription: true,
            originalWorkDescription: true,
            electronicSignature: true,
        });

        if (Object.keys(newErrors).length > 0) {
            setValidated(true);
            return;
        }

        setValidated(true);
        setLoading(true);
        setSubmitError('');

        try {
            const response = await submitTakedown(formData, captchaToken);

            setRequestId(response.requestId);
            setSubmitSuccess(true);
        } catch (err) {
            setSubmitError(
                err.message ||
                    'Failed to submit takedown request. Please try again.'
            );
        } finally {
            setLoading(false);
            captchaRef.current?.reset();
            setCaptchaToken(null);
        }
    };

    // Show success message
    if (submitSuccess) {
        return (
            <Form className="mx-auto text-center">
                <Row className="mb-3 form-header">
                    <h1 className="form-title h1 text-primary">
                        Request Submitted
                    </h1>
                    <p className="form-description h6">
                        Your takedown request has been submitted successfully.
                    </p>
                </Row>
                <Alert variant="info" className="text-start">
                    <Alert.Heading className="h6">
                        Your Request ID
                    </Alert.Heading>
                    <p className="mb-2">
                        <code className="fs-5">{requestId}</code>
                    </p>
                    <hr />
                    <p className="mb-0 small">
                        Please save this ID for your records. You can use it to
                        check the status of your request. We will review your
                        submission and respond within 24-72 hours.
                    </p>
                </Alert>
            </Form>
        );
    }

    return (
        <Form noValidate onSubmit={handleSubmit} className="mx-auto">
            {/* Header */}
            <Row className="mb-3 form-header">
                <h1 className="form-title h1 text-primary">
                    Content Takedown Request
                </h1>
                <p className="form-description h6 text-primary">
                    Use this form to submit a DMCA takedown request for content
                    that infringes on your copyright. All fields marked with *
                    are required.
                </p>
            </Row>

            {/* Important Notice */}
            <Alert variant="warning" className="mb-4">
                <strong>Important:</strong> This form is for legitimate
                copyright claims only. Filing a false DMCA notice may result in
                legal consequences. Please review our{' '}
                <Alert.Link href="/policies/takedown">
                    Content Takedown Policy
                </Alert.Link>{' '}
                for more information.
            </Alert>

            {/* Error Message */}
            {submitError && (
                <div className="alert alert-danger text-center" role="alert">
                    {submitError}
                </div>
            )}

            {/* Section: Requester Information */}
            <h2 className="h5 text-primary mb-3 mt-4">Your Information</h2>

            {/* Name Field */}
            <Row className="mb-3">
                <Form.Group as={Col} md="12">
                    <InputGroup hasValidation className="input-log-label">
                        <FloatingLabel
                            controlId="floatingRequesterName"
                            label="Full Legal Name *"
                        >
                            <Form.Control
                                type="text"
                                name="requesterName"
                                placeholder="Your full legal name"
                                value={formData.requesterName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                    touched.requesterName &&
                                    !!errors.requesterName
                                }
                                isValid={
                                    touched.requesterName &&
                                    !errors.requesterName &&
                                    formData.requesterName.trim() !== ''
                                }
                                disabled={loading}
                            />
                        </FloatingLabel>
                    </InputGroup>
                    {validated && errors.requesterName && (
                        <div className="invalid-feedback d-block fw-semibold ps-3">
                            {errors.requesterName}
                        </div>
                    )}
                </Form.Group>
            </Row>

            {/* Email Field */}
            <Row className="mb-3">
                <Form.Group as={Col} md="12">
                    <InputGroup hasValidation className="input-log-label">
                        <FloatingLabel
                            controlId="floatingRequesterEmail"
                            label="Email Address *"
                        >
                            <Form.Control
                                type="email"
                                name="requesterEmail"
                                placeholder="your@email.com"
                                value={formData.requesterEmail}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                    touched.requesterEmail &&
                                    !!errors.requesterEmail
                                }
                                isValid={
                                    touched.requesterEmail &&
                                    !errors.requesterEmail &&
                                    formData.requesterEmail.trim() !== ''
                                }
                                disabled={loading}
                            />
                        </FloatingLabel>
                    </InputGroup>
                    {validated && errors.requesterEmail && (
                        <div className="invalid-feedback d-block fw-semibold ps-3">
                            {errors.requesterEmail}
                        </div>
                    )}
                </Form.Group>
            </Row>

            {/* Address Field */}
            <Row className="mb-3">
                <Form.Group as={Col} md="12">
                    <InputGroup hasValidation className="input-log-label">
                        <FloatingLabel
                            controlId="floatingRequesterAddress"
                            label="Full Mailing Address *"
                        >
                            <Form.Control
                                as="textarea"
                                name="requesterAddress"
                                placeholder="Your full mailing address *"
                                value={formData.requesterAddress}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                    touched.requesterAddress &&
                                    !!errors.requesterAddress
                                }
                                isValid={
                                    touched.requesterAddress &&
                                    !errors.requesterAddress &&
                                    formData.requesterAddress.trim() !== ''
                                }
                                disabled={loading}
                                className="message-textarea"
                                rows={3}
                            />
                        </FloatingLabel>
                    </InputGroup>
                    {validated && errors.requesterAddress && (
                        <div className="invalid-feedback d-block fw-semibold ps-3">
                            {errors.requesterAddress}
                        </div>
                    )}
                </Form.Group>
            </Row>

            {/* Role Field */}
            <Row className="mb-3">
                <Form.Group as={Col} md="12">
                    <InputGroup hasValidation className="input-log-label">
                        <FloatingLabel
                            controlId="floatingRequesterRole"
                            label="Your Relationship to the Copyright *"
                        >
                            <Form.Select
                                name="requesterRole"
                                value={formData.requesterRole}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                    touched.requesterRole &&
                                    !!errors.requesterRole
                                }
                                isValid={
                                    touched.requesterRole &&
                                    !errors.requesterRole &&
                                    formData.requesterRole !== ''
                                }
                                disabled={loading}
                            >
                                <option value="">
                                    Select your relationship...
                                </option>
                                {REQUESTER_ROLES.map((role) => (
                                    <option key={role.value} value={role.value}>
                                        {role.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </FloatingLabel>
                    </InputGroup>
                    {validated && errors.requesterRole && (
                        <div className="invalid-feedback d-block fw-semibold ps-3">
                            {errors.requesterRole}
                        </div>
                    )}
                </Form.Group>
            </Row>

            {/* Copyright Owner Name (if agent) */}
            {formData.requesterRole === 'agent' && (
                <Row className="mb-3">
                    <Form.Group as={Col} md="12">
                        <InputGroup hasValidation className="input-log-label">
                            <FloatingLabel
                                controlId="floatingCopyrightOwnerName"
                                label="Copyright Owner's Name *"
                            >
                                <Form.Control
                                    type="text"
                                    name="copyrightOwnerName"
                                    placeholder="Name of the copyright owner"
                                    value={formData.copyrightOwnerName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={
                                        touched.copyrightOwnerName &&
                                        !!errors.copyrightOwnerName
                                    }
                                    isValid={
                                        touched.copyrightOwnerName &&
                                        !errors.copyrightOwnerName &&
                                        formData.copyrightOwnerName.trim() !==
                                            ''
                                    }
                                    disabled={loading}
                                />
                            </FloatingLabel>
                        </InputGroup>
                        {validated && errors.copyrightOwnerName && (
                            <div className="invalid-feedback d-block fw-semibold ps-3">
                                {errors.copyrightOwnerName}
                            </div>
                        )}
                    </Form.Group>
                </Row>
            )}

            {/* Section: Infringing Content */}
            <h2 className="h5 text-primary mb-3 mt-4">Infringing Content</h2>

            {/* Content URL Field */}
            <Row className="mb-3">
                <Form.Group as={Col} md="12">
                    <InputGroup hasValidation className="input-log-label">
                        <FloatingLabel
                            controlId="floatingContentUrl"
                            label="URL of Infringing Content *"
                        >
                            <Form.Control
                                type="url"
                                name="contentUrl"
                                placeholder="https://..."
                                value={formData.contentUrl}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                    touched.contentUrl && !!errors.contentUrl
                                }
                                isValid={
                                    touched.contentUrl &&
                                    !errors.contentUrl &&
                                    formData.contentUrl.trim() !== ''
                                }
                                disabled={loading}
                            />
                        </FloatingLabel>
                    </InputGroup>
                    {validated && errors.contentUrl && (
                        <div className="invalid-feedback d-block fw-semibold ps-3">
                            {errors.contentUrl}
                        </div>
                    )}
                </Form.Group>
            </Row>

            {/* Content Description Field */}
            <Row className="mb-3">
                <Form.Group as={Col} md="12" controlId="contentDescription">
                    <Form.Label className="text-muted small mb-1">
                        Description of Infringing Content *
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        name="contentDescription"
                        placeholder="Please describe the infringing content in detail..."
                        value={formData.contentDescription}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                            touched.contentDescription &&
                            !!errors.contentDescription
                        }
                        isValid={
                            touched.contentDescription &&
                            !errors.contentDescription &&
                            formData.contentDescription.trim() !== ''
                        }
                        disabled={loading}
                        className="message-textarea"
                    />
                    {validated && errors.contentDescription && (
                        <div className="invalid-feedback d-block fw-semibold">
                            {errors.contentDescription}
                        </div>
                    )}
                </Form.Group>
            </Row>

            {/* Section: Original Work */}
            <h2 className="h5 text-primary mb-3 mt-4">Your Original Work</h2>

            {/* Original Work Description Field */}
            <Row className="mb-3">
                <Form.Group
                    as={Col}
                    md="12"
                    controlId="originalWorkDescription"
                >
                    <Form.Label className="text-muted small mb-1">
                        Description of Your Copyrighted Work *
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        name="originalWorkDescription"
                        placeholder="Please describe your original copyrighted work that has been infringed..."
                        value={formData.originalWorkDescription}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                            touched.originalWorkDescription &&
                            !!errors.originalWorkDescription
                        }
                        isValid={
                            touched.originalWorkDescription &&
                            !errors.originalWorkDescription &&
                            formData.originalWorkDescription.trim() !== ''
                        }
                        disabled={loading}
                        className="message-textarea"
                    />
                    {validated && errors.originalWorkDescription && (
                        <div className="invalid-feedback d-block fw-semibold">
                            {errors.originalWorkDescription}
                        </div>
                    )}
                </Form.Group>
            </Row>

            {/* Original Work URL Field (Optional) */}
            <Row className="mb-3">
                <Form.Group as={Col} md="12">
                    <InputGroup className="input-log-label">
                        <FloatingLabel
                            controlId="floatingOriginalWorkUrl"
                            label="URL of Original Work (Optional)"
                        >
                            <Form.Control
                                type="url"
                                name="originalWorkUrl"
                                placeholder="https://..."
                                value={formData.originalWorkUrl}
                                onChange={handleChange}
                                disabled={loading}
                            />
                        </FloatingLabel>
                    </InputGroup>
                </Form.Group>
            </Row>

            {/* Evidence Upload */}
            <Row className="mb-3">
                <Form.Group as={Col} md="12" controlId="evidenceUpload">
                    <div className="screenshot-upload">
                        <Form.Label className="text-muted mb-2">
                            Supporting Evidence (Optional)
                        </Form.Label>
                        <Form.Control
                            type="file"
                            ref={fileInputRef}
                            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                            onChange={handleFileChange}
                            disabled={loading}
                            className="border-0 p-0"
                        />
                        <Form.Text className="text-muted">
                            Max 10MB. Accepted formats: JPEG, PNG, GIF, WebP,
                            PDF. You may attach proof of ownership or other
                            evidence.
                        </Form.Text>
                        {formData.evidence && (
                            <div className="mt-3">
                                {evidencePreview ? (
                                    <Image
                                        src={evidencePreview}
                                        alt="Evidence preview"
                                        className="screenshot-preview"
                                    />
                                ) : (
                                    <span className="text-muted">
                                        {formData.evidence.name}
                                    </span>
                                )}
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="ms-3"
                                    onClick={handleRemoveEvidence}
                                    aria-label="Remove evidence file"
                                >
                                    Remove
                                </Button>
                            </div>
                        )}
                    </div>
                </Form.Group>
            </Row>

            {/* Section: Legal Statements */}
            <h2 className="h5 text-primary mb-3 mt-4">Legal Statements</h2>

            {/* Good Faith Statement */}
            <Row className="mb-3">
                <Form.Group as={Col} md="12">
                    <Form.Check
                        type="checkbox"
                        id="goodFaithStatement"
                        name="goodFaithStatement"
                        checked={formData.goodFaithStatement}
                        onChange={handleChange}
                        isInvalid={validated && !!errors.goodFaithStatement}
                        disabled={loading}
                        label={
                            <span className="small">
                                I have a good faith belief that the use of the
                                copyrighted material described above is not
                                authorized by the copyright owner, its agent, or
                                the law (e.g., as a fair use). *
                            </span>
                        }
                    />
                    {validated && errors.goodFaithStatement && (
                        <div className="invalid-feedback d-block fw-semibold">
                            {errors.goodFaithStatement}
                        </div>
                    )}
                </Form.Group>
            </Row>

            {/* Accuracy Statement */}
            <Row className="mb-3">
                <Form.Group as={Col} md="12">
                    <Form.Check
                        type="checkbox"
                        id="accuracyStatement"
                        name="accuracyStatement"
                        checked={formData.accuracyStatement}
                        onChange={handleChange}
                        isInvalid={validated && !!errors.accuracyStatement}
                        disabled={loading}
                        label={
                            <span className="small">
                                I swear,{' '}
                                <strong>under penalty of perjury</strong>, that
                                the information in this notification is accurate
                                and that I am the copyright owner, or am
                                authorized to act on behalf of the owner, of an
                                exclusive right that is allegedly infringed. *
                            </span>
                        }
                    />
                    {validated && errors.accuracyStatement && (
                        <div className="invalid-feedback d-block fw-semibold">
                            {errors.accuracyStatement}
                        </div>
                    )}
                </Form.Group>
            </Row>

            {/* Electronic Signature */}
            <Row className="mb-4">
                <Form.Group as={Col} md="12">
                    <Form.Label className="text-muted small mb-2">
                        Electronic Signature *
                    </Form.Label>
                    <div
                        className={`signature-canvas-container position-relative border rounded ${
                            validated && errors.electronicSignature
                                ? 'border-danger'
                                : touched.electronicSignature &&
                                    !errors.electronicSignature &&
                                    formData.electronicSignature
                                  ? 'border-success'
                                  : ''
                        }`}
                    >
                        <canvas
                            ref={canvasRef}
                            className="signature-canvas"
                            aria-label="Electronic signature canvas - draw your signature here"
                        />
                        <Button
                            variant="primary"
                            size="sm"
                            className="signature-clear-btn"
                            onClick={handleClearSignature}
                            disabled={loading}
                            type="button"
                            aria-label="Clear signature"
                        >
                            Clear
                        </Button>
                    </div>
                    <Form.Text className="text-muted mt-2 d-block">
                        Sign above using your mouse or touch screen. This
                        signature has the same legal effect as a handwritten
                        signature.
                    </Form.Text>
                    {validated && errors.electronicSignature && (
                        <div className="invalid-feedback d-block fw-semibold">
                            {errors.electronicSignature}
                        </div>
                    )}
                </Form.Group>
            </Row>

            {/* Captcha */}
            <Row className="mb-4 justify-content-center">
                <Col xs="auto">
                    <Captcha
                        ref={captchaRef}
                        onVerify={setCaptchaToken}
                        theme="light"
                        size="normal"
                        onExpired={() => setCaptchaToken(null)}
                        onError={() => setCaptchaToken(null)}
                    />
                </Col>
            </Row>

            {/* Submit Button */}
            <Button
                className="form-send-btn fw-bold"
                type="submit"
                disabled={loading || !captchaToken}
            >
                {loading ? (
                    <>
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                        />
                        Submitting...
                    </>
                ) : (
                    'Submit Takedown Request'
                )}
            </Button>
        </Form>
    );
}
